import { useEffect } from 'react';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { customerVehiclesServices } from '@services';
import { showError } from '@utils';
import {
  normalizeBundles,
  normalizeBundleTypes,
  normalizeCustomerVehicle,
  normalizeCustomerVehicleUsers,
} from './utils';

export const useCustomerVehicleOperations = (enableFetch = true) => {
  const router = useRouter();

  const customerVehicleId = router?.query?.vehicleId || '';

  const customerVehicleResponse = useQuery(
    [QUERY_KEYS.GET_CUSTOMER_VEHICLE_KEY, { customerVehicleId }],
    () => customerVehiclesServices.getCustomerVehicleById(customerVehicleId),
    { enabled: enableFetch, refetchOnMount: true }
  );

  const usersResponse = useQuery(
    [QUERY_KEYS.GET_CUSTOMER_VEHICLE_USERS_KEY],
    () =>
      customerVehiclesServices.getCustomerVehicleUsers(customerVehicleId, {
        pageSize: 100,
      }),
    {
      enabled: enableFetch && customerVehicleResponse.isFetched,
      refetchOnMount: true,
    }
  );

  const bundleTypesResponse = useQuery(
    [QUERY_KEYS.GET_BUNDLE_TYPES, { customerVehicleId }],
    () => customerVehiclesServices.getBundleTypes({ page: 1, pageSize: 100 }),
    {
      enabled: enableFetch,
      refetchOnMount: true,
    }
  );

  const bundlesResponse = useQuery(
    [QUERY_KEYS.GET_BUNDLES, { customerVehicleId }],
    () =>
      customerVehiclesServices.getBundles({
        page: 1,
        pageSize: 100,
      }),
    {
      enabled: enableFetch && customerVehicleResponse.isFetched,
      refetchOnMount: true,
    }
  );

  const customerVehicle = normalizeCustomerVehicle(
    customerVehicleResponse?.data?.data || {}
  );

  const customerVehicleUsers = normalizeCustomerVehicleUsers(
    usersResponse?.data?.data || []
  );

  const bundleTypes = normalizeBundleTypes(
    bundleTypesResponse?.data?.data || []
  );

  const bundles = normalizeBundles(
    bundlesResponse?.data?.data || [],
    customerVehicleId
  );

  const loading =
    customerVehicleResponse?.isLoading ||
    usersResponse?.isLoading ||
    bundlesResponse?.isLoading ||
    bundleTypesResponse?.isLoading;

  const refetchData = () => {
    customerVehicleResponse.refetch();
    usersResponse.refetch();
    bundlesResponse.refetch();
  };

  useEffect(() => {
    if (customerVehicleResponse.isError) {
      showError(customerVehicleResponse?.error);
    }
  }, [customerVehicleResponse.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (usersResponse.isError) {
      showError(usersResponse?.error);
    }
  }, [usersResponse.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    loading,
    customerVehicle,
    customerVehicleId,
    getCustomerVehicle: refetchData,
    customerVehicleUsers,
    bundleTypes,
    getBundleTypes: bundleTypesResponse.refetch,
    bundles,
    getBundles: bundlesResponse.refetch,
  };
};
